import { Inject, Injectable, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { intersection } from 'lodash-es';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';
import { Login, LoginFromAnonymous, Logout } from '../../state/auth/actions';
import { User } from '../../state/auth/types';
import { AppState } from '../../state/state.module';
import { BackendInterceptor } from '../backend-interceptor/backend-interceptor.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private lastLoginAttempt: { username: string; password: string } = null;
  loginLowerCaseSensitive = false;

  constructor(
    protected store: Store<AppState>,
    protected backendInterceptor: BackendInterceptor,
    @Optional()
    protected oauthService: OAuthService,
    @Optional()
    @Inject('environment')
    protected environment: { [s: string]: any }
  ) {
    this.loginLowerCaseSensitive = this.backendInterceptor.getEnvironmentProperty('loginLowerCaseSensitive') || false;
  }

  loginUser(userName: string, password: string, fromAnonymous?: boolean, totpCode?: string): void {
    if (this.loginLowerCaseSensitive) {
      userName = userName.toLowerCase();
    }

    if (!totpCode) {
      this.lastLoginAttempt = { username: userName, password };
    }

    const payload = {
      username: totpCode ? this.lastLoginAttempt?.username || userName : userName,
      password: totpCode ? this.lastLoginAttempt?.password || password : password,
      ...(totpCode ? { totpCode } : {})
    };

    const action = fromAnonymous ? new LoginFromAnonymous(payload) : new Login(payload);
    this.store.dispatch(action);
  }

  getCurrentUser(): Observable<User> {
    return this.store.select(state => state.auth.user);
  }

  getIsAuthenticated(blockAnonymous: boolean = false): Observable<boolean> {
    return this.store
      .select(state => state.auth.authenticated)
      .pipe(
        switchMap(authenticated => {
          if (blockAnonymous) {
            return this.getRoles().pipe(map(roles => !roles.includes('ANONYMOUS')));
          }
          return of(authenticated);
        })
      );
  }

  getRoles(): Observable<string[]> {
    return this.store
      .select(state => state.auth.user)
      .pipe(
        filter(user => !!user),
        switchMap(user =>
          user.roles.includes('ACTUATOR') && this.mockRolesModeEnabled()
            ? this.getMockRoles().pipe(first())
            : of(user.roles)
        )
      );
  }

  getUserActiveGroups(): Observable<any> {
    return this.store
      .select(state => state.auth.user)
      .pipe(
        filter(user => !!user),
        map(user => user.activeGroups)
      );
  }

  getHasRoles(role: string): Observable<boolean> {
    return this.store.select(
      state => !!state?.auth?.user?.roles && state.auth.user.roles.filter(r => r === role).length !== 0
    );
  }

  getIsBadAuth(): Observable<boolean> {
    return this.store.select(state => state.auth.badLogin);
  }

  getInternalServerError(): Observable<boolean> {
    return this.store.select(state => state.auth.internalServerError);
  }

  getIsUserDisabled(): Observable<boolean> {
    return this.store.select(state => state.auth.userDisabled);
  }

  getIsForceChangePassword(): Observable<boolean> {
    return this.store.select(state => state.auth.userForceChangePassword);
  }

  getMfaInfo(): Observable<{ required: boolean; state: 'setup' | 'code' | null }> {
    return this.store.select(state => ({
      required: state.auth.mfaRequired,
      state: state.auth.mfaState
    }));
  }

  logout() {
    if (this.environment?.oauth2Enabled) {
      this.oauthService.logOut();
    }

    this.store.dispatch(new Logout());
  }

  mockRolesModeEnabled() {
    return !!localStorage.getItem('mockRoles');
  }

  getMockRoles(): Observable<string[]> {
    return this.getCurrentUser().pipe(
      first(),
      map(user => {
        if (user.roles.includes('ANONYMOUS') || !user.roles.includes('ACTUATOR')) {
          localStorage.removeItem('mockRoles');
          return user.roles;
        }
        const mockRoles = JSON.parse(localStorage.getItem('mockRoles'));
        return intersection(user.roles || [], mockRoles);
      })
    );
  }

  toggleUseMockRoles() {
    if (this.mockRolesModeEnabled()) {
      localStorage.removeItem('mockRoles');
      location.reload();
    } else {
      this.getRoles()
        .pipe(first())
        .subscribe(roles => {
          localStorage.setItem('mockRoles', JSON.stringify(roles));
          location.reload();
        });
    }
  }

  toggleMockRole(role: string) {
    this.getMockRoles().subscribe(currentMockRoles => {
      if (currentMockRoles.includes(role)) {
        currentMockRoles.splice(currentMockRoles.indexOf(role), 1);
        localStorage.setItem('mockRoles', JSON.stringify(currentMockRoles));
        location.reload();
      } else {
        this.getCurrentUser()
          .pipe(first())
          .subscribe(user => {
            if (user.roles.includes(role)) {
              currentMockRoles.push(role);
              localStorage.setItem('mockRoles', JSON.stringify(currentMockRoles));
            } else {
              console.error('CANNOT ENABLE ROLE NOT ON USER');
            }
            location.reload();
          });
      }
    });
  }
}
